import React, { useEffect, useState } from 'react';
import { Bell, ExternalLink, ShieldAlert, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { fetchAlertNotifications } from '../api';
import './AlertNotifications.css';

export const notificationLink = (event) => `/kloud-ops/centro-operaciones?tab=alertas&alert=${encodeURIComponent(event.alert_fingerprint)}`;

function NotificationFeed({ identity }) {
  const navigate = useNavigate();
  const [eligible, setEligible] = useState(false);
  const [events, setEvents] = useState([]);
  const [toasts, setToasts] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const controller = new AbortController();
    const storageKey = `ops-alert-cursor:${identity}`;
    let cursor;
    try {
      const saved = sessionStorage.getItem(storageKey);
      if (saved !== null && /^\d+$/.test(saved)) cursor = Number(saved);
    } catch {}
    let timer;
    let cancelled = false;
    const poll = async () => {
      try {
        const initial = cursor == null;
        const response = await fetchAlertNotifications(cursor, controller.signal);
        if (cancelled) return;
        setEligible(true);
        const incoming = response.events || [];
        setEvents((previous) => [...incoming.slice().reverse(), ...previous.filter((event) => !incoming.some((item) => item.event_id === event.event_id))].slice(0, 50));
        if (!initial && incoming.length) {
          setToasts((previous) => [...incoming.slice().reverse(), ...previous].slice(0, 3));
          setUnread((previous) => previous + incoming.length);
        }
        cursor = response.cursor;
        try { sessionStorage.setItem(storageKey, String(cursor)); } catch {}
      } catch (error) {
        if (cancelled) return;
        if ([401, 403].includes(error.response?.status)) {
          setEligible(false);
          setEvents([]);
          setToasts([]);
          return;
        }
      }
      if (!cancelled) timer = setTimeout(poll, 15000);
    };
    poll();
    return () => { cancelled = true; clearTimeout(timer); controller.abort(); };
  }, [identity]);

  useEffect(() => {
    if (!toasts.length) return;
    const timer = setTimeout(() => setToasts([]), 15000);
    return () => clearTimeout(timer);
  }, [toasts]);

  if (!eligible) return null;
  const visit = (event) => {
    setOpen(false);
    setToasts((previous) => previous.filter((item) => item.event_id !== event.event_id));
    navigate(notificationLink(event));
  };
  const renderEvent = (event, popup) => (
    <article key={event.event_id} className="ops-notification" data-severity={event.severity.toUpperCase()}>
      <div className="ops-notification-heading">
        <ShieldAlert size={18} aria-hidden="true" />
        <span>{event.severity} · {event.provider.toUpperCase()}</span>
        {popup && <button type="button" title="Cerrar aviso" aria-label="Cerrar aviso" onClick={() => setToasts((previous) => previous.filter((item) => item.event_id !== event.event_id))}><X size={16} /></button>}
      </div>
      {event.customer && <p className="ops-notification-customer">{event.customer}</p>}
      <p className="ops-notification-title">{event.title}</p>
      {event.resource && <p className="ops-notification-resource">{event.resource}</p>}
      <div className="ops-notification-footer">
        <time>{event.event_ts && !Number.isNaN(Date.parse(event.event_ts)) ? new Date(event.event_ts).toLocaleString('es-MX') : ''}</time>
        <button type="button" onClick={() => visit(event)}><ExternalLink size={14} />Ver alerta</button>
      </div>
    </article>
  );

  return (
    <aside className="ops-notifications" aria-label="Notificaciones de alertas">
      {!open && <div className="ops-notification-popups" aria-live="polite">{toasts.map((event) => renderEvent(event, true))}</div>}
      {open && <section className="ops-notification-panel" aria-label="Alertas recientes">
        <header><h2>Alertas recientes</h2><button type="button" title="Cerrar notificaciones" aria-label="Cerrar notificaciones" onClick={() => setOpen(false)}><X size={18} /></button></header>
        <div className="ops-notification-list">{events.length ? events.map((event) => renderEvent(event, false)) : <p className="ops-notification-empty">Sin alertas recientes</p>}</div>
      </section>}
      <button type="button" className="ops-notification-bell" title="Notificaciones de alertas" aria-label={`Notificaciones de alertas${unread ? `, ${unread} nuevas` : ''}`} aria-expanded={open} onClick={() => { setOpen(!open); setUnread(0); setToasts([]); }}>
        <Bell size={22} />{unread > 0 && <span>{unread > 99 ? '99+' : unread}</span>}
      </button>
    </aside>
  );
}

export default function AlertNotifications() {
  const { user } = useAuth();
  const identity = user?.email || user?.id;
  return identity ? <NotificationFeed key={identity} identity={identity} /> : null;
}